import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import { useUsers } from "../../hooks/useUsers";
import {
  useOrganisationRoles,
  useUpdateOrganisationMemberRole,
} from "../../hooks/useOrganisations";
import { useSnackbar } from "../../contexts/SnackbarContext";

const OrganisationMemberRoleDialog = ({
  open,
  handleClose,
  organisationId,
  userId,
}) => {
  const { showSuccess, showError } = useSnackbar();
  const { data: users } = useUsers();

  const { data: roles } = useOrganisationRoles(organisationId);
  const updateRoleMutation = useUpdateOrganisationMemberRole();

  const [roleData, setRoleData] = useState({
    admin_permission: false,
    add_permission: false,
    edit_permission: false,
    delete_permission: false,
    transfer_permission: false,
  });

  const user = users?.find((u) => u.id === parseInt(userId));
  const currentRole = roles?.find((r) => r.user_id === parseInt(userId));

  useEffect(() => {
    if (currentRole) {
      setRoleData({
        admin_permission: currentRole.admin_permission || false,
        add_permission: currentRole.add_permission || false,
        edit_permission: currentRole.edit_permission || false,
        delete_permission: currentRole.delete_permission || false,
        transfer_permission: currentRole.transfer_permission || false,
      });
    } else {
      // Default to view only (no permissions)
      setRoleData({
        admin_permission: false,
        add_permission: false,
        edit_permission: false,
        delete_permission: false,
        transfer_permission: false,
      });
    }
  }, [currentRole]);

  const handlePermissionChange = (permission, checked) => {
    if (permission === "admin_permission") {
      if (checked) {
        // Admin gets all permissions
        setRoleData({
          admin_permission: true,
          add_permission: true,
          edit_permission: true,
          delete_permission: true,
          transfer_permission: true,
        });
      } else {
        // Remove admin and all other permissions
        setRoleData({
          admin_permission: false,
          add_permission: false,
          edit_permission: false,
          delete_permission: false,
          transfer_permission: false,
        });
      }
    } else {
      // If any other permission is being removed, also remove admin
      const newRoleData = {
        ...roleData,
        [permission]: checked,
      };

      if (!checked) {
        newRoleData.admin_permission = false;
      }

      setRoleData(newRoleData);
    }
  };

  const handleSave = () => {
    updateRoleMutation.mutate(
      {
        organisationId: parseInt(organisationId),
        userId: parseInt(userId),
        roleData: {
          organisation_id: parseInt(organisationId),
          user_id: parseInt(userId),
          ...roleData,
        },
      },
      {
        onSuccess: () => {
          showSuccess("Member role updated successfully");
          handleClose();
        },
        onError: (error) => {
          showError(
            `Failed to update member role: ${
              error.response?.data?.detail || error.message
            }`
          );
        },
      }
    );
  };

  const isViewOnly = !Object.values(roleData).some(Boolean);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box>
          <Typography variant="h6" component="div">
            Edit Member Role
          </Typography>
          {user && (
            <Typography variant="body2" color="text.secondary">
              {user.email}
            </Typography>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ py: 2 }}>
          <Typography variant="h6" gutterBottom>
            Permissions
          </Typography>

          {isViewOnly && (
            <Alert severity="info" sx={{ mb: 2 }}>
              View Only - No permissions assigned
            </Alert>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={roleData.admin_permission}
                onChange={(e) =>
                  handlePermissionChange("admin_permission", e.target.checked)
                }
                color="primary"
              />
            }
            label={
              <Box>
                <Typography fontWeight="bold">Administrator</Typography>
                <Typography variant="caption" color="text.secondary">
                  Full access to all organisation features
                </Typography>
              </Box>
            }
          />

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom color="text.secondary">
            Specific Permissions
          </Typography>

          <Box sx={{ ml: 2, display: "flex", flexDirection: "column" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={roleData.add_permission}
                  onChange={(e) =>
                    handlePermissionChange("add_permission", e.target.checked)
                  }
                  disabled={roleData.admin_permission}
                />
              }
              label="Add devices and applications"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={roleData.edit_permission}
                  onChange={(e) =>
                    handlePermissionChange("edit_permission", e.target.checked)
                  }
                  disabled={roleData.admin_permission}
                />
              }
              label="Edit devices and applications"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={roleData.delete_permission}
                  onChange={(e) =>
                    handlePermissionChange(
                      "delete_permission",
                      e.target.checked
                    )
                  }
                  disabled={roleData.admin_permission}
                />
              }
              label="Delete devices and applications"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={roleData.transfer_permission}
                  onChange={(e) =>
                    handlePermissionChange(
                      "transfer_permission",
                      e.target.checked
                    )
                  }
                  disabled={roleData.admin_permission}
                />
              }
              label="Transfer device ownership"
            />
          </Box>

          {updateRoleMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Failed to update role: {updateRoleMutation.error?.message}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={updateRoleMutation.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={updateRoleMutation.isPending}
        >
          {updateRoleMutation.isPending ? (
            <CircularProgress size={20} />
          ) : (
            "Save"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrganisationMemberRoleDialog;
